import {EntryList} from "@client/components/EntryList";
import {RecipeSummary} from "@client/components/RecipeSummary";
import {useCapture} from "@client/features/camera";
import * as recipes from "@client/features/recipes";
import * as search from "@client/features/search";
import * as forms from "@client/forms";
import * as router from "@client/router";
import {RecipeShare} from "@client/templates/Share";
import {server} from "@reactivated";
import * as React from "react";
import {match} from "ts-pattern";

export const UpdateRecipe = router.UpdateRecipe.withHooks((props) => {
    const recipe = props.resolved.update_recipe;
    const isOwner = props.profile.user.id == recipe.user_id;

    const capture = useCapture({
        target: {type: "RECIPE", recipe},
        profile: props.profile,
        cameraEnabled: false,
    });

    return {
        recipe,
        capture,
        ...recipes.useRecipeForm({
            initial: {
                ...recipe,
                serving_size_unit: recipe.serving_size_unit ?? "",
                container_unit: recipe.container_unit as "g",
            },
        }),
        isOwner,
    };
})
    .withTab(
        {
            name: "recipe",
            icon: "cutlery",
            title: "Recipe",
        },
        (props) =>
            props.isOwner == false ? (
                <div className="-mt-2">
                    <RecipeShare
                        type="embed"
                        share={{
                            type: "recipe",
                            share_uuid: props.recipe.uuid,
                            recipe: props.recipe,
                            is_favorite: false,
                        }}
                    />
                </div>
            ) : (
                <div>
                    <EntryList
                        createLink={(
                            entry, // Assuming isOwner is passed from .withHooks
                        ) =>
                            router.reverse("update_recipe_entry.macros", {
                                recipeId: props.recipe.id,
                                entryUUID: entry.uuid,
                            })
                        }
                        entries={props.recipe.expanded_recipe}
                        hiddenMacros={props.profile.has_hidden_macros}
                    />
                    {props.profile.has_hidden_macros === false && (
                        <RecipeSummary
                            useKilojoules={props.profile.use_kilojoules}
                            recipe={props.recipe}
                        />
                    )}
                </div>
            ),
    )
    .withTab(
        {
            name: "details",
            icon: "pencil-square-o",
            title: "Details",
            guard: (props) => (props.isOwner ? props : false),
            hasErrors: (props) => props.layout.hasErrors,
        },
        (props) => props.layout.component,
    )
    .withActions((props) =>
        props.isOwner == false
            ? [
                  {
                      icon: "copy",
                      title: "Copy",
                      onClick: async () => {
                          const response = await server.journal.nutrition.copy_recipe({
                              recipe_id: props.recipe.id,
                          });
                          if (response.type === "success") {
                              void props.dispatch(search.loadSearch({reset: true}));
                              props.notify(
                                  "Recipe copied. Switched to the copy so you can make changes.",
                              );
                              void router.push("update_recipe.details", {
                                  recipeId: response.data.id,
                              });
                          }
                      },
                  },
              ]
            : [
                  {
                      icon: "remove",
                      onClick: () =>
                          props.dispatch(recipes.confirmAndDeleteRecipe(props.recipe)),
                      title: "Delete",
                  },
                  {
                      icon: "plus",
                      onClick: async () => {
                          const result = await props.capture.start({
                              initialView: "manual",
                              cameraMode: "barcode_only",
                          });

                          const entries = await match(result)
                              .with({type: "barcode"}, async (r) => {
                                  const resolved = await props.capture.resolveBarcode(
                                      r.barcode,
                                  );
                                  return resolved?.entries ?? null;
                              })
                              .with({type: "entries"}, (r) => r.entries)
                              .otherwise(() => null);

                          if (entries != null) {
                              props.setResolved(() => ({
                                  update_recipe: recipes.addEntriesToRecipe({
                                      recipe: props.recipe,
                                      entries,
                                  }),
                              }));
                          }
                      },
                      title: "Ingredient",
                  },
                  {
                      icon: "copy",
                      title: "Copy",
                      onClick: async () => {
                          const response = await server.journal.nutrition.copy_recipe({
                              recipe_id: props.recipe.id,
                          });
                          if (response.type === "success") {
                              void props.dispatch(search.loadSearch({reset: true}));
                              props.notify(
                                  "Recipe copied. Switched to the copy so you can make changes.",
                              );
                              void router.push("update_recipe.details", {
                                  recipeId: response.data.id,
                              });
                          }
                      },
                  },
                  {
                      icon: "share",
                      title: "Share",
                      onClick: async () => {
                          const response = await server.journal.nutrition.share_recipe({
                              recipe_uuid: props.recipe.uuid,
                          });

                          if (response.type === "success") {
                              props.dispatch({
                                  type: "SHARE_SUCCESS",
                                  share: {
                                      type: "recipe",
                                      share_uuid: response.data.share_uuid,
                                  },
                              });
                          }
                      },
                  },
                  {
                      icon: "save",
                      onClick: async () => {
                          const response = await forms.submitForm(props.form, (data) =>
                              server.journal.nutrition.update_recipe_metadata({
                                  ...data,
                                  recipe_id: props.recipe.id,
                              }),
                          );
                          if (response?.type === "success") {
                              void props.dispatch(search.loadSearch({reset: true}));
                              props.notify("Your recipe was updated");
                              void router.push("recipe_list.index", {});
                          }
                      },
                      title: "Save",
                  },
              ],
    )
    .withOptions((props) => ({
        title: "Update Recipe",
        back: "Recipes",
        dialogs: props.capture.dialog,
    }));
